import { useState, useEffect, useMemo } from 'preact/hooks'
import { canUseMockData } from '@/constants/env'
import type { DashboardRange } from '@/types/dashboard'
import { DASHBOARD_RANGE_OPTIONS } from '@/types/dashboard'
import { workspaceApi } from '@/api/workspace'
import { useAnalyticsView } from '@/hooks/useAnalyticsView'
import { useCompany } from '@/hooks/useCompany'
import { useDashboardTiles } from '@/hooks/useDashboardTiles'
import { useWorkspaceDealAnalytics } from '@/hooks/useHubSpotDealAnalytics'
import { TotalLeadsStatTile } from '@/components/dashboard/TotalLeadsStatTile'
import { CompaniesEngagedStatTile } from '@/components/dashboard/CompaniesEngagedStatTile'
import { ActiveCampaignsStatTile } from '@/components/dashboard/ActiveCampaignsStatTile'
import { ActiveWebsStatTile } from '@/components/dashboard/ActiveWebsStatTile'
import { LandingPageViewsStatTile } from '@/components/dashboard/LandingPageViewsStatTile'
import { UniqueLandingPageViewsStatTile } from '@/components/dashboard/UniqueLandingPageViewsStatTile'
import { ConversionsStatTile } from '@/components/dashboard/ConversionsStatTile'
import { VisitorFunnelStatTile } from '@/components/dashboard/VisitorFunnelStatTile'
import { PipelineGeneratedStatTile } from '@/components/dashboard/PipelineGeneratedStatTile'
import { ClosedWonStatTile } from '@/components/dashboard/ClosedWonStatTile'
import { NewestConversionsTile } from '@/components/dashboard/NewestConversionsTile'
import { HottestCompaniesTile } from '@/components/dashboard/HottestCompaniesTile'
import { CampaignPerformanceTile } from '@/components/dashboard/CampaignPerformanceTile'
import { TopPerformingWebsTile } from '@/components/dashboard/TopPerformingWebsTile'
import { HottestLeadsTile } from '@/components/dashboard/HottestLeadsTile'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatViewModes } from '@/constants/aiChat'
import { timeFromNowVerbose } from '@/utils/time'

interface DashboardProps {
  selectedWorkspaceId: string
}

export function Dashboard({ selectedWorkspaceId: propWorkspaceId }: DashboardProps) {
  const { companyId, currentWorkspaceId } = useCompany()
  const workspaceId = propWorkspaceId || currentWorkspaceId || ''
  const dealAnalytics = useWorkspaceDealAnalytics(workspaceId)

  // All Time is the default: a workspace's first weeks otherwise read as near-empty, which is
  // what made "Companies Engaged" look broken when it was correctly reporting the last 30 days.
  const [range, setRange] = useState<DashboardRange>('AllTime')
  const [useMock, setUseMock] = useState(canUseMockData())
  const showMockToggle = canUseMockData()
  const { updateContext } = useAIChatContextUpdater()

  // The window each figure covers, captioned on the tiles so a number can be read — or quoted —
  // without hunting for the range control at the top of the page.
  const rangeLabel = DASHBOARD_RANGE_OPTIONS.find(option => option.value === range)?.label ?? range

  // Every tile below comes from this one request.
  const { tiles, sections, loading: tilesLoading, refreshing: tilesRefreshing, error: tilesError, refresh: refreshTiles, sectionFailed } = useDashboardTiles({
    workspaceId,
    range,
    mock: useMock,
  })

  // When the numbers were computed. The bundle is cached server-side, so "now" would be a claim the
  // page cannot make — and the header previously advertised real-time refresh that never happens.
  const generatedRelative = tiles ? timeFromNowVerbose(tiles.GeneratedAtUtc) : null

  // Visitor analytics for this workspace. Fetched once here and passed to the three
  // analytics tiles (the endpoint returns all metrics in one AnalyticsViewDto).
  const analyticsFetcher = useMemo(
    () => (workspaceId ? () => workspaceApi.getAnalytics(workspaceId, range, useMock) : null),
    [workspaceId, range, useMock],
  )
  const { data: analytics, loading: analyticsLoading, error: analyticsError } = useAnalyticsView(analyticsFetcher)

  // Update AI context for workspace dashboard
  useEffect(() => {
    updateContext({
      location: ChatLocations.WorkspaceDashboard,
      viewMode: ChatViewModes.View,
      availableFields: [
        { name: 'dateRange', displayName: 'Date Range', fieldType: 'select', currentValue: range, isEditable: false, isFocused: false },
      ],
      availableActions: [],
    })
  }, [workspaceId, range, updateContext])

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Analytics Dashboard</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {generatedRelative ? `Generated: ${generatedRelative}` : ' '}
            </p>
          </div>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
            {showMockToggle && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Use Mock Data</span>
                <button
                  type="button"
                  onClick={() => setUseMock(m => !m)}
                  aria-pressed={useMock}
                  aria-label="Toggle mock data"
                  className={`relative inline-flex h-5 w-9 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800 ${useMock ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white dark:bg-slate-200 shadow transform transition-transform duration-200 ${useMock ? 'translate-x-4' : ''}`}
                  />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Range</label>
              <select
                value={range}
                onChange={e => setRange(e.currentTarget.value as DashboardRange)}
                className="select-range transition-default"
              >
                {DASHBOARD_RANGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {tilesError && (
        <div
          role="alert"
          className="mb-6 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-900/20"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Couldn't load the dashboard</p>
            <p className="text-2xs text-red-600/80 dark:text-red-400/80 break-words">{tilesError}</p>
          </div>
          <button
            type="button"
            onClick={refreshTiles}
            className="shrink-0 rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40"
          >
            Retry
          </button>
        </div>
      )}
      <div className={tilesRefreshing ? "opacity-60 transition-opacity pointer-events-none" : "transition-opacity"}>
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {/* Row 1: lead/pipeline metrics */}
        <TotalLeadsStatTile rangeLabel={rangeLabel} count={sections?.TotalLeadsGenerated} loading={tilesLoading} failed={sectionFailed('TotalLeadsGenerated')} />
        <CompaniesEngagedStatTile rangeLabel={rangeLabel} count={sections?.CompaniesEngaged} loading={tilesLoading} failed={sectionFailed('CompaniesEngaged')} />
        <ActiveCampaignsStatTile rangeLabel="Right now" count={sections?.ActiveCampaignCount} loading={tilesLoading} failed={sectionFailed('ActiveCampaignCount')} />
        <PipelineGeneratedStatTile data={dealAnalytics.data} loading={dealAnalytics.loading} error={dealAnalytics.error} />
        <ClosedWonStatTile data={dealAnalytics.data} loading={dealAnalytics.loading} error={dealAnalytics.error} />
        {/* Row 2: webs + visitor analytics, capped by the funnel snapshot */}
        <ActiveWebsStatTile rangeLabel="Right now" count={sections?.ActiveWebCount} loading={tilesLoading} failed={sectionFailed('ActiveWebCount')} />
        <LandingPageViewsStatTile metric={analytics?.LandingPageViews ?? null} loading={analyticsLoading} error={analyticsError} />
        <UniqueLandingPageViewsStatTile metric={analytics?.UniqueLandingPageViews ?? null} loading={analyticsLoading} error={analyticsError} />
        <ConversionsStatTile metric={analytics?.Conversions ?? null} loading={analyticsLoading} error={analyticsError} />
        <VisitorFunnelStatTile analytics={analytics} loading={analyticsLoading} error={analyticsError} />
      </div>
      {/* Tall tiles row */}
      <div className="grid gap-5 grid-cols-1 md:grid-cols-2 mt-8">
        <NewestConversionsTile conversions={sections?.NewestConversions} clientCompanyId={companyId ?? ''} workspaceId={workspaceId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('NewestConversions')} mock={useMock} />
        <HottestCompaniesTile companies={sections?.HottestCompanies} workspaceId={workspaceId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('HottestCompanies')} mock={useMock} />
      </div>
      {/* Top performing webs + hottest leads row */}
      <div className="grid gap-5 grid-cols-1 md:grid-cols-2 mt-8">
        <TopPerformingWebsTile webs={sections?.TopPerformingWebs} workspaceId={workspaceId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('TopPerformingWebs')} mock={useMock} />
        <HottestLeadsTile leads={sections?.HottestLeads} clientCompanyId={companyId ?? ''} workspaceId={workspaceId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('HottestLeads')} mock={useMock} />
      </div>
      {/* Full-width close: the roll-up of every row above it. */}
      <div className="mt-8">
        <CampaignPerformanceTile campaigns={sections?.CampaignPerformance} workspaceId={workspaceId} loading={tilesLoading} failed={sectionFailed('CampaignPerformance')} mock={useMock} />
      </div>
      </div>
    </div>
  )
}
